// pages/PaymentCallback.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logger from '../utils/logger';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference');
      const trxref = searchParams.get('trxref');
      
      // Use reference from URL params
      const paymentRef = reference || trxref;
      
      if (!paymentRef) {
        setStatus('error');
        setError('No payment reference found');
        return;
      }

      try {
        const response = await fetch(`https://www.whydiditdie.com//api/payments/verify/${paymentRef}`);
        const data = await response.json();

        if (data.status) {
          setStatus('success');
          setPaymentData(data.data);
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setStatus('failed');
          setError(data.message || 'Payment verification failed');
        }
      } catch (err) {
        setStatus('error');
        setError('Failed to verify payment');
        logger.error('Payment verification error:', err);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  const getStatusContent = () => {
    switch (status) {
      case 'verifying':
        return {
          icon: '⏳',
          title: 'Verifying Payment...',
          message: 'Please wait while we confirm your payment.',
          color: '#f59e0b'
        };
      case 'success':
        return {
          icon: '✅',
          title: 'Payment Successful!',
          message: `Welcome to ${paymentData?.plan || 'your new'} plan! You'll be redirected to your dashboard shortly.`,
          color: '#10b981'
        };
      case 'failed':
        return {
          icon: '❌',
          title: 'Payment Failed',
          message: error || 'Your payment could not be processed.',
          color: '#ef4444'
        };
      case 'error':
        return {
          icon: '⚠️',
          title: 'Verification Error',
          message: error || 'There was an error verifying your payment.',
          color: '#ef4444'
        };
      default:
        return {
          icon: '❓',
          title: 'Unknown Status',
          message: 'Something unexpected happened.',
          color: '#6b7280'
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        padding: '3rem 2rem',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          marginBottom: '2rem'
        }}>
          <span style={{ fontSize: '1.8rem' }}>💀</span>
          <span style={{
            fontSize: '1.5rem',
            fontWeight: '900',
            color: '#e07a5f',
            letterSpacing: '-0.5px'
          }}>
            WhyDidItDie
          </span>
        </div>

        {/* Status Icon */}
        <div style={{
          fontSize: '4rem',
          marginBottom: '1.5rem',
          animation: status === 'verifying' ? 'pulse 2s infinite' : 'none'
        }}>
          {statusContent.icon}
        </div>

        {/* Status Title */}
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: statusContent.color
        }}>
          {statusContent.title}
        </h1>

        {/* Status Message */}
        <p style={{
          fontSize: '1.1rem',
          color: 'rgba(255, 255, 255, 0.8)',
          marginBottom: '2rem',
          lineHeight: '1.6'
        }}>
          {statusContent.message}
        </p>

        {/* Payment Details (if successful) */}
        {status === 'success' && paymentData && (
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            <h3 style={{
              color: '#10b981',
              marginBottom: '1rem',
              fontSize: '1.1rem'
            }}>
              Payment Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Plan:</span>
                <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {paymentData.plan}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Amount:</span>
                <span style={{ fontWeight: 'bold' }}>
                  ${paymentData.amount}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Billing:</span>
                <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {paymentData.billing_cycle}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Reference:</span>
                <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.9rem',
                  color: '#10b981'
                }}>
                  {paymentData.reference}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {status === 'success' && (
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                backgroundColor: '#10b981',
                border: 'none',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#059669';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#10b981';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Go to Dashboard
            </button>
          )}

          {(status === 'failed' || status === 'error') && (
            <>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  backgroundColor: '#e07a5f',
                  border: 'none',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#cc6f53';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#e07a5f';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Contact Support
              </button>
            </>
          )}

          {status === 'verifying' && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.9rem'
            }}>
              This may take a few moments...
            </div>
          )}
        </div>

        {/* Loading indicator for verifying state */}
        {status === 'verifying' && (
          <div style={{
            marginTop: '2rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(245, 158, 11, 0.3)',
              borderTop: '3px solid #f59e0b',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentCallback;